import * as Api from '@ViewModels';
import {
	FileSendMergeFieldSymbols,
	FileSendMergeFieldValue,
	textRequestsFiles,
	textSendsFiles,
} from '../../extViewmodels/fileLinkMergeFields';
import { IRichContentDocumentEditorPlaceholder } from '../index';
import {
	DefaultMeetingEmailPlaceholders,
	FirstNamePlaceholder,
	GetDefaultEmailPlaceholders,
	MeetingDatePlaceholder,
	MeetingDayPlaceholder,
	MeetingTimePlaceholder,
	MeetingTimeZonePlaceholder,
	SenderFirstNamePlaceholder,
	Token,
} from '../Token';

/**
 * Replaces token with actual first name
 */
export const replaceTokenWithActualFirstName = (
	email: Api.EmailMessageViewModel<File>,
	contact: Api.ContactViewModel
) => {
	if (contact.firstName) {
		try {
			const document = Token.replaceToken(email?.content?.document, FirstNamePlaceholder, contact.firstName);
			if (document) {
				email.content = {
					...email.content,
					document,
				};
			}
		} catch {
			// swallow this (There was no token or it was invalid)
		}
	}

	return email;
};

/**
 * Replaces token with actual first name in raw content state
 */
export const replaceTokenWithActualFirstNameString = (rawContent: Api.IRawRichTextContentState, firstName: string) => {
	if (firstName) {
		try {
			const document = Token.replaceToken(rawContent?.document, FirstNamePlaceholder, firstName);
			if (document) {
				rawContent = {
					...rawContent,
					document,
				};
			}
		} catch {
			// swallow this (There was no token or it was invalid)
		}
	}

	return rawContent;
};

/**
 * First name HTML token
 */
export const firstNameHtml = new Token(FirstNamePlaceholder).toHTMLString();

/**
 * Default rich content for email automation step
 */
export const defaultRichContentForEmailAutomationStep = Api.createRawRichTextContentStateWithText(
	`Hi ${firstNameHtml},`
);

/**
 * Gets rich content message for texting step
 */
export const getRichContentMessageForTextingStep = (
	userSession: Api.UserSessionContext,
	trigger: Api.IAutomationTrigger,
	schedule: Api.IAutomationStepSchedule
) => {
	const placeholders: IRichContentDocumentEditorPlaceholder[] = [
		...GetDefaultEmailPlaceholders(userSession),
		...DefaultMeetingEmailPlaceholders,
	];

	const senderFirstNameHtml = new Token(
		placeholders.find(x => x.text === SenderFirstNamePlaceholder.text)
	).toHTMLString();
	const meetingDateHtml = new Token(placeholders.find(x => x.text === MeetingDatePlaceholder.text)).toHTMLString();
	const meetingDayHtml = new Token(placeholders.find(x => x.text === MeetingDayPlaceholder.text)).toHTMLString();
	const meetingTimeHtml = new Token(placeholders.find(x => x.text === MeetingTimePlaceholder.text)).toHTMLString();
	const meetingTimeZoneHtml = new Token(
		placeholders.find(x => x.text === MeetingTimeZonePlaceholder.text)
	).toHTMLString();

	const greetings = `Hi ${firstNameHtml},<br/><br/>`;
	const closing = `Best,<br/>${senderFirstNameHtml}`;

	const content: string =
		trigger?._type !== Api.AutomationTriggerType.Meeting || schedule?.numberOfDays > 0 || schedule?.numberOfHours > 0
			? greetings
			: `${greetings} ${
					(schedule?.numberOfDays >= -1 && schedule?.numberOfDays !== 0) ||
					(schedule?.numberOfHours >= -24 && schedule?.numberOfHours !== 0)
						? `Just wanted to remind you that our meeting is ${meetingDayHtml}, ${meetingDateHtml} at ${meetingTimeHtml}(${meetingTimeZoneHtml}).<br/><br/>Let me know if you need to make changes.<br/><br/>Look forward to connecting.`
						: `I am looking forward to our meeting ${meetingDayHtml}, ${meetingDateHtml} at ${meetingTimeHtml}(${meetingTimeZoneHtml}).<br/><br/>Please let me know if you need to reschedule.<br/><br/>Look forward to connecting.`
				}<br/><br/>${closing}`;
	return content;
};

/**
 * Gets default rich content for texting step based on trigger type
 */
export const getDefaultRichContentForTextingStep = (
	account?: Api.IAccount,
	trigger?: Api.IAutomationTrigger,
	userSession?: Api.UserSessionContext,
	schedule?: Api.IAutomationStepSchedule
) => {
	const firstNameToken = new Token(FirstNamePlaceholder);
	const senderNameToken = new Token(SenderFirstNamePlaceholder);
	let messageHtmlStringValue = `Hi ${firstNameToken.toHTMLString()}, this is ${senderNameToken.toHTMLString()} from ${account?.companyName}.`;
	switch (trigger?._type) {
		case Api.AutomationTriggerType.NewLead: {
			messageHtmlStringValue = `${messageHtmlStringValue}\nYou can text me at this number if you are still looking for assistance.`;
			break;
		}
		case Api.AutomationTriggerType.NewClient:
		case Api.AutomationTriggerType.NewDonor:
		case Api.AutomationTriggerType.NewMatterTrigger: {
			messageHtmlStringValue = `${messageHtmlStringValue}\nYou can text me at this number in the future should you need anything!`;
			break;
		}
		case Api.AutomationTriggerType.MatterStageChangedTrigger:
		case Api.AutomationTriggerType.MatterStatusChangedTrigger: {
			messageHtmlStringValue = `${messageHtmlStringValue}\nJust wanted to let you know there's been an update to your matter. Please reach out if you have any questions.`;
			break;
		}
		case Api.AutomationTriggerType.ServiceJobCompletedTrigger: {
			messageHtmlStringValue = `${messageHtmlStringValue}\nJust wanted to follow up on your recently completed service job. Please reach out if you have any questions.`;
			break;
		}
		case Api.AutomationTriggerType.Meeting: {
			messageHtmlStringValue = getRichContentMessageForTextingStep(userSession, trigger, schedule);
			break;
		}
		case Api.AutomationTriggerType.ResourceSelector: {
			const t = trigger as Api.IResourceSelectorAutomationTrigger;
			switch (t.resourceSelector) {
				case Api.ResourceSelectorId.HappyBirthday:
				case Api.ResourceSelectorId.Turning65:
				case Api.ResourceSelectorId.Turning72:
				case Api.ResourceSelectorId.Turning73: {
					messageHtmlStringValue = `${messageHtmlStringValue}\nJust wanted to wish you a happy birthday!`;
					break;
				}
				case Api.ResourceSelectorId.PolicyRenew:
					messageHtmlStringValue = `${messageHtmlStringValue}\nJust a reminder that one of your policies is up for renewals. Please text with any questions.`;
					break;
				default: {
					break;
				}
			}
			break;
		}
		default: {
			break;
		}
	}
	return messageHtmlStringValue ? Api.createRawRichTextContentStateWithText(messageHtmlStringValue) : null;
};

export function textHasFileLinkToken(text: string | undefined): boolean {
	return textSendsFiles(text);
}

export function contentHasFileLinkToken(content: Api.IRawRichTextContentState | undefined): boolean {
	return !!content && textSendsFiles(Api.getContentHtmlStringValueFromRawRichTextContentState(content));
}

export function removeFileLinkTokens(html: string): string {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const affectedParagraphs = new Set<HTMLParagraphElement>();
	const textNodes = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
	for (let node = textNodes.nextNode(); node; node = textNodes.nextNode()) {
		const text = node.textContent ?? '';
		const withoutSymbols = FileSendMergeFieldSymbols.reduce((result, symbol) => result.split(symbol).join(''), text);
		if (withoutSymbols !== text) {
			node.textContent = withoutSymbols;
			const paragraph = node.parentElement?.closest('p');
			if (paragraph) {
				affectedParagraphs.add(paragraph);
			}
		}
	}
	doc.querySelectorAll(`span[data-placeholder-value="${FileSendMergeFieldValue}"]`).forEach(chip => {
		const paragraph = chip.closest('p');
		if (paragraph) {
			affectedParagraphs.add(paragraph);
		}
		chip.remove();
	});
	affectedParagraphs.forEach(paragraph => {
		if (!paragraph.textContent?.trim() && !paragraph.querySelector('img')) {
			paragraph.remove();
		}
	});
	return doc.body.innerHTML;
}

export function contentHasFileRequestToken(content: Api.IRawRichTextContentState | undefined): boolean {
	return !!content && textRequestsFiles(Api.getContentHtmlStringValueFromRawRichTextContentState(content));
}

export function textHasFileRequestToken(text: string | undefined): boolean {
	return textRequestsFiles(text);
}
